/**
 * AASB 8 — Operating Segments. Rulepack v1.0.0.
 * Validates segment-level data against the 10% quantitative thresholds and
 * the 75% external-revenue coverage rule. Aggregation correctness needs
 * judgement and is flagged as info.
 */
import { Finding, ValidationResult, DISCLAIMER } from './types';
import {
  CellValue, parseNumber, parseBool, makeHeaderMap, normaliseHeader,
  acceptedHeadersFor, findingBuilder, severityCounts, compliantCount, fmt,
} from './_shared';

const VERSION = 'aasb8-1.0.0';
const fb = findingBuilder('AASB_8' as any, VERSION);

const HMAP = makeHeaderMap([
  ['segment_id', 'segment id', 'segment', 'segment name', 'name'],
  ['external_revenue', 'external revenue', 'revenue from external customers', 'external'],
  ['intersegment_revenue', 'intersegment revenue', 'inter-segment revenue', 'internal'],
  ['total_revenue', 'total revenue', 'segment revenue'],
  ['segment_profit_loss', 'segment profit', 'segment loss', 'segment p/l', 'segment profit/loss'],
  ['segment_assets', 'segment assets', 'assets'],
  ['segment_liabilities', 'segment liabilities', 'liabilities'],
  ['reported_separately', 'reported separately', 'reportable', 'is reportable'],
  ['reviewed_by_codm', 'reviewed by codm', 'codm', 'codm reviewed'],
]);

const REQUIRED = ['segment_id', 'external_revenue'];

export function detectAASB8(headers: string[]) {
  const hits = new Set<string>();
  for (const r of headers) { const c = normaliseHeader(HMAP, String(r ?? '')); if (c) hits.add(c); }
  const matched = REQUIRED.filter(r => hits.has(r));
  const boost = ['segment', 'codm', 'intersegment'].some(k => headers.some(h => String(h ?? '').toLowerCase().includes(k)));
  return { confidence: Math.min(1, (matched.length / REQUIRED.length) * (boost ? 1.5 : 1)), matched, missing: REQUIRED.filter(r => !hits.has(r)) };
}

export function validateAASB8(headers: string[], data: CellValue[][], _asAt: Date = new Date()): ValidationResult {
  const findings: Finding[] = [];
  const schemaErrors: string[] = [];
  const idx: Record<string, number> = {};
  headers.forEach((h, i) => { const c = normaliseHeader(HMAP, String(h ?? '')); if (c) idx[c] = i; });
  for (const f of REQUIRED) if (idx[f] === undefined) schemaErrors.push(`Missing column "${f}". Accepted: ${acceptedHeadersFor(HMAP, f).join(', ')}.`);
  if (schemaErrors.length) {
    return { summary: { standard: 'AASB_8' as any, rulepackVersion: VERSION, rowsTested: 0, compliant: 0, byseverity: { info: 0, warning: 0, error: 0 }, runAt: new Date().toISOString() }, findings, schemaErrors, disclaimer: DISCLAIMER };
  }

  // Pass 1: parse rows + compute totals
  const segs: Array<{
    id: string; rowNum: number;
    extRev: number; interRev: number; totalRev: number;
    profit: number; assets: number; liabilities: number;
    reportedSep: boolean | null; codm: boolean | null;
  }> = [];
  let totalExtRev = 0, totalProfitAbs = 0, totalAssets = 0;
  const rowIds: string[] = [];

  data.forEach((raw, i) => {
    const rowNum = i + 2;
    const get = (k: string) => raw[idx[k]];
    const id = String(get('segment_id') ?? `row-${rowNum}`);
    rowIds.push(id);

    const missing = REQUIRED.filter(f => { const v = get(f); return v == null || v === ''; });
    if (missing.length) { findings.push(fb.build('AASB8.R01', 'error', 'Required field missing', `Row ${rowNum} (${id}): missing ${missing.join(', ')}.`, { rowId: id })); return; }

    const extRev = parseNumber(get('external_revenue')) ?? 0;
    const interRev = idx.intersegment_revenue !== undefined ? (parseNumber(get('intersegment_revenue')) ?? 0) : 0;
    const totalRev = idx.total_revenue !== undefined ? (parseNumber(get('total_revenue')) ?? (extRev + interRev)) : (extRev + interRev);
    const profit = idx.segment_profit_loss !== undefined ? (parseNumber(get('segment_profit_loss')) ?? 0) : 0;
    const assets = idx.segment_assets !== undefined ? (parseNumber(get('segment_assets')) ?? 0) : 0;
    const liabilities = idx.segment_liabilities !== undefined ? (parseNumber(get('segment_liabilities')) ?? 0) : 0;
    const reportedSep = idx.reported_separately !== undefined ? parseBool(get('reported_separately')) : null;
    const codm = idx.reviewed_by_codm !== undefined ? parseBool(get('reviewed_by_codm')) : null;

    segs.push({ id, rowNum, extRev, interRev, totalRev, profit, assets, liabilities, reportedSep, codm });
    totalExtRev += extRev;
    totalProfitAbs += Math.abs(profit);
    totalAssets += assets;

    // R02 totalRev internal consistency
    if (idx.total_revenue !== undefined) {
      const reported = parseNumber(get('total_revenue')) ?? 0;
      if (Math.abs(reported - (extRev + interRev)) > 50) {
        findings.push(fb.build('AASB8.R02', 'warning', 'Total revenue does not equal external + intersegment',
          `Segment ${id}: reported total $${fmt(reported)}, sum $${fmt(extRev + interRev)}.`,
          { rowId: id }));
      }
    }
  });

  // Pass 2: 10% threshold check + 75% external revenue coverage + CODM consistency
  let extRevReportable = 0;
  for (const s of segs) {
    const revPct = totalExtRev > 0 ? (s.extRev + s.interRev) / (totalExtRev + segs.reduce((sum, x) => sum + x.interRev, 0)) : 0;
    const profitPct = totalProfitAbs > 0 ? Math.abs(s.profit) / totalProfitAbs : 0;
    const assetPct = totalAssets > 0 ? s.assets / totalAssets : 0;
    const meetsThreshold = revPct >= 0.10 || profitPct >= 0.10 || assetPct >= 0.10;

    if (meetsThreshold && s.reportedSep === false) {
      findings.push(fb.build('AASB8.R03', 'error', 'Segment exceeds 10% threshold but not reported separately',
        `Segment ${s.id}: revenue ${(revPct * 100).toFixed(1)}% / profit ${(profitPct * 100).toFixed(1)}% / assets ${(assetPct * 100).toFixed(1)}% — at least one ≥ 10%. AASB 8.13 requires separate reporting.`,
        { rowId: s.id, evidence: { revPct, profitPct, assetPct } }));
    }
    if (s.reportedSep === true) {
      extRevReportable += s.extRev;
    }

    // R04 CODM consistency
    if (s.codm === false && s.reportedSep === true) {
      findings.push(fb.build('AASB8.R04', 'warning', 'Segment reported separately but not reviewed by CODM',
        `Segment ${s.id}: reportedSeparately=true but reviewed_by_codm=false. AASB 8 segments are based on what the CODM reviews.`,
        { rowId: s.id }));
    }
  }

  // R05 75% external-revenue coverage rule
  if (totalExtRev > 0) {
    const coverage = extRevReportable / totalExtRev;
    if (coverage < 0.75) {
      findings.push(fb.build('AASB8.R05', 'warning', 'Reportable segments cover <75% of external revenue',
        `External revenue covered by reportable segments: ${(coverage * 100).toFixed(1)}%. AASB 8.15 requires additional segments to be identified until coverage ≥ 75%.`,
        { evidence: { coverage, totalExtRev, extRevReportable } }));
    }
  }

  return {
    summary: { standard: 'AASB_8' as any, rulepackVersion: VERSION, rowsTested: rowIds.length, compliant: compliantCount(rowIds, findings), byseverity: severityCounts(findings), runAt: new Date().toISOString() },
    findings, disclaimer: DISCLAIMER,
  };
}
